import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { GripVertical, ImageIcon } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MediaCard } from './MediaCard';
import type { AmbientMedia } from '@/services/ambientPhotoService';

interface SortableAmbientPhotosGridProps {
  photos: AmbientMedia[];
  onDelete: (photoId: string) => Promise<void>;
  loading?: boolean;
  onCropSuccess?: () => void;
  onReorder: (photoIds: string[]) => Promise<void>;
}

interface SortablePhotoCardProps {
  photo: AmbientMedia;
  onDelete: (photoId: string) => Promise<void>;
  onCropSuccess?: () => void;
  position: number;
}

function SortablePhotoCard({ photo, onDelete, onCropSuccess, position }: SortablePhotoCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'z-50 scale-105 shadow-2xl' : ''}`}
      {...attributes}
    >
      <div className="absolute bottom-2 left-2 z-10 bg-content-primary text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
        {position}
      </div>
      <button
        {...listeners}
        className="absolute bottom-2 right-2 z-10 cursor-grab active:cursor-grabbing p-1.5 bg-white/90 hover:bg-white rounded shadow-md transition-colors"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical className="h-4 w-4 text-gray-700" />
      </button>
      <MediaCard
        media={photo}
        onDelete={onDelete}
        onCropSuccess={onCropSuccess}
      />
    </div>
  );
}

export function SortableAmbientPhotosGrid({
  photos,
  onDelete,
  loading = false,
  onCropSuccess,
  onReorder,
}: SortableAmbientPhotosGridProps) {
  const [items, setItems] = useState<AmbientMedia[]>(photos);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    setItems(photos);
  }, [photos]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      setIsReordering(true);

      try {
        const photoIds = newItems.map((item) => item.blob_id);
        await onReorder(photoIds);
        toast.success('Orden de fotos actualizado');
      } catch {
        toast.error('Error al guardar el orden');
        setItems(photos);
      } finally {
        setIsReordering(false);
      }
    }
  };

  if (!loading && items.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No hay fotos de ambiente
        </h3>
        <p className="text-gray-500">
          Sube fotos de tu restaurante que se incluirán al final de todos los videos generados.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="aspect-[9/16] rounded-lg bg-gray-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((photo, index) => (
              <SortablePhotoCard
                key={photo.id}
                photo={photo}
                onDelete={onDelete}
                onCropSuccess={onCropSuccess}
                position={index + 1}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {isReordering && (
        <div className="flex items-center justify-center py-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
          Guardando orden...
        </div>
      )}
    </div>
  );
}
